export type ListCopy = {
  // the noun, for composing
  noun: string
  nounPlural: string
  Noun: string
  NounPlural: string
  // workflow builder
  addToList: string
  moveListStage: string
  placeContactInList: string
  selectAList: string
  noListSelected: string
  listToMoveWithin: string
  notInThisListYet: string
  addContactToListIssue: string
  chooseAList: string
  stageAboveThisStep: string
  thisList: string
  updateList: string
  movedToListStage: string
  contactMovesIntoListStage: string
  stageConditionPredatesLists: string
  // contact surfaces
  addToAList: string
  allListsAdded: string
  couldNotUpdateList: string
  errorLoadingList: string
  failedToUpdateList: string
  contactAddedToList: string
  errorAddingContactToList: string
  selectAListAndStage: string
  selectAListPlaceholder: string
  removeFromList: string
  alreadyInThisList: string
  listStage: string
  allContactsEmptyDescription: string
  // workflow builder: field label
  listStageField: string
  // pickers
  filterListsHelper: string | undefined
  // settings and roles
  manualListsPermission: string
  staffDescription: string
}

// Byte-identical to today's source strings, pasted from the G5 inventory: the toggle-off path is
// what every user sees until `grouped-navigation` ships, so nothing here may be retyped.
const pipelineCopy: ListCopy = {
  noun: `pipeline`,
  nounPlural: `pipelines`,
  Noun: `Pipeline`,
  NounPlural: `Pipelines`,
  addToList: `Add to pipeline`,
  moveListStage: `Move pipeline stage`,
  placeContactInList: `Place the contact in a pipeline`,
  selectAList: `Select a pipeline…`,
  noListSelected: `No pipeline selected`,
  listToMoveWithin: `The pipeline to move the contact within.`,
  notInThisListYet: `A contact who isn't in this pipeline yet is added here.`,
  addContactToListIssue: `add the contact to a pipeline`,
  chooseAList: `Choose a pipeline.`,
  stageAboveThisStep: `their pipeline stage above this step if that isn’t what you want.`,
  thisList: `this pipeline`,
  updateList: `Update pipeline`,
  movedToListStage: `Moved to pipeline stage`,
  contactMovesIntoListStage: `A contact moves into a pipeline stage`,
  stageConditionPredatesLists: `This stage condition was saved before stages knew which pipeline they belong to, and a stage name on its own cannot enroll anyone. Pick the stage again from the list to fix it.`,
  addToAList: `Add to a pipeline`,
  allListsAdded: `All pipelines added`,
  couldNotUpdateList: `Couldn't update pipeline. Change reverted.`,
  errorLoadingList: `Error loading pipeline`,
  failedToUpdateList: `Failed to update pipeline`,
  contactAddedToList: `Contact added to pipeline`,
  errorAddingContactToList: `There was an error adding the contact to the pipeline`,
  selectAListAndStage: `Please select a pipeline and stage`,
  selectAListPlaceholder: `Select a pipeline`,
  removeFromList: `Remove from pipeline`,
  alreadyInThisList: `Already in this pipeline`,
  listStage: `Pipeline Stage`,
  allContactsEmptyDescription: `Contacts arrive from your calls, forms, and PMS sync. Once they land here you can filter them, work them through a pipeline, and text them from the inbox.`,
  listStageField: `Pipeline stage`,
  filterListsHelper: undefined,
  manualListsPermission: `Pipelines`,
  staffDescription: `Add the people who work at this practice so calls, tasks, and pipeline contacts can be assigned to them by name.`,
}

const listWordingCopy: ListCopy = {
  noun: `list`,
  nounPlural: `lists`,
  Noun: `List`,
  NounPlural: `Lists`,
  addToList: `Add to list`,
  moveListStage: `Move list stage`,
  placeContactInList: `Place the contact in a list`,
  selectAList: `Select a list…`,
  noListSelected: `No list selected`,
  listToMoveWithin: `The list to move the contact within.`,
  notInThisListYet: `A contact who isn't in this list yet is added here.`,
  addContactToListIssue: `add the contact to a list`,
  chooseAList: `Choose a list.`,
  stageAboveThisStep: `their list stage above this step if that isn't what you want.`,
  thisList: `this list`,
  updateList: `Update list`,
  movedToListStage: `Moved to list stage`,
  contactMovesIntoListStage: `A contact moves into a list stage`,
  stageConditionPredatesLists: `This stage condition was saved before stages knew which list they belong to, and a stage name on its own cannot enroll anyone. Pick the stage again from the picker to fix it.`,
  addToAList: `Add to a list`,
  allListsAdded: `All lists added`,
  couldNotUpdateList: `Couldn't update list. Change reverted.`,
  errorLoadingList: `Error loading list`,
  failedToUpdateList: `Failed to update list`,
  contactAddedToList: `Contact added to list`,
  errorAddingContactToList: `There was an error adding the contact to the list`,
  selectAListAndStage: `Please select a list and stage`,
  selectAListPlaceholder: `Select a list`,
  removeFromList: `Remove from list`,
  alreadyInThisList: `Already in this list`,
  listStage: `List Stage`,
  allContactsEmptyDescription: `Contacts arrive from your calls, forms, and PMS sync. Once they land here you can filter them, work them through a list, and text them from the inbox.`,
  listStageField: `List stage`,
  filterListsHelper: `Filter lists update themselves, so there is no one to add.`,
  manualListsPermission: `Manual lists`,
  staffDescription: `Add the people who work at this practice so calls, tasks, and list contacts can be assigned to them by name.`,
}

/** Every user-facing string that names the pipeline/list concept, picked by the `grouped-navigation`
 *  toggle. Two literal records, not per-key ternaries, so the pipeline wording can be deleted as one
 *  block at rollout. */
export function listCopy(listsOn: boolean): ListCopy {
  return listsOn ? listWordingCopy : pipelineCopy
}
